// Типы для MediaPipe Selfie Segmentation
interface SegmentationResults {
  image: HTMLCanvasElement | HTMLImageElement | ImageBitmap;
  segmentationMask: HTMLCanvasElement | HTMLImageElement | ImageBitmap;
}

interface SelfieSegmentationInstance {
  close(): Promise<void>;
  onResults(listener: (results: SegmentationResults) => void): void;
  initialize(): Promise<void>;
  reset(): void;
  send(inputs: { image: HTMLVideoElement }): Promise<void>;
  setOptions(options: { selfieMode?: boolean; modelSelection?: number }): void;
}

interface SelfieSegmentationConstructor {
  new (config: { locateFile: (file: string) => string }): SelfieSegmentationInstance;
}

declare global {
  interface Window {
    SelfieSegmentation?: SelfieSegmentationConstructor;
  }
}

// Загрузка скрипта MediaPipe
let scriptLoadPromise: Promise<void> | null = null;
function loadMediaPipeScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;
  
  if (window.SelfieSegmentation) {
    return Promise.resolve();
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load MediaPipe script'));
    document.head.appendChild(script);
  });
  
  return scriptLoadPromise;
}

export class BackgroundBlurProcessor {
  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private blurCanvas: HTMLCanvasElement | null = null;
  private blurCtx: CanvasRenderingContext2D | null = null;
  private selfieSegmentation: SelfieSegmentationInstance | null = null;
  private animationId: number | null = null;
  private isRunning = false;
  private processedStream: MediaStream | null = null;
  private blurAmount: number;

  constructor(blurAmount = 15) {
    this.blurAmount = blurAmount;
  }

  async start(sourceStream: MediaStream): Promise<MediaStream> {
    if (this.isRunning) {
      await this.stop();
    }

    const videoTrack = sourceStream.getVideoTracks()[0];
    if (!videoTrack) {
      throw new Error('No video track in source stream');
    }

    this.isRunning = true;

    // Создаем video элемент
    this.video = document.createElement('video');
    this.video.srcObject = sourceStream;
    this.video.muted = true;
    this.video.playsInline = true;
    await this.video.play();

    // Создаем canvas
    const settings = videoTrack.getSettings();
    const width = settings.width || 640;
    const height = settings.height || 480;

    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });

    this.blurCanvas = document.createElement('canvas');
    this.blurCanvas.width = width;
    this.blurCanvas.height = height;
    this.blurCtx = this.blurCanvas.getContext('2d');

    if (!this.ctx || !this.blurCtx) {
      throw new Error('Failed to get canvas context');
    }

    // Загружаем MediaPipe
    await loadMediaPipeScript();
    
    if (!window.SelfieSegmentation) {
      throw new Error('MediaPipe SelfieSegmentation not loaded');
    }

    this.selfieSegmentation = new window.SelfieSegmentation({
      locateFile: (file: string) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
      }
    });

    this.selfieSegmentation.setOptions({
      modelSelection: 1,
      selfieMode: true,
    });

    // Обработчик результатов
    this.selfieSegmentation.onResults((results: SegmentationResults) => {
      if (!this.ctx || !this.blurCtx || !this.canvas || !this.blurCanvas || !this.isRunning) return;

      const { width, height } = this.canvas;

      // Очищаем
      this.ctx.clearRect(0, 0, width, height);

      // Размытый фон
      this.blurCtx.filter = `blur(${this.blurAmount}px)`;
      this.blurCtx.drawImage(results.image, 0, 0, width, height);
      this.blurCtx.filter = 'none';
      this.ctx.drawImage(this.blurCanvas, 0, 0);

      // Вырезаем человека из размытого фона
      this.ctx.save();
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.drawImage(results.segmentationMask, 0, 0, width, height);
      this.ctx.restore();

      // Рисуем четкого человека
      this.ctx.save();
      this.ctx.globalCompositeOperation = 'destination-over';
      this.ctx.drawImage(results.image, 0, 0, width, height);
      this.ctx.restore();
    });

    await this.selfieSegmentation.initialize();

    // Запускаем обработку кадров
    const processFrame = async () => {
      if (!this.isRunning || !this.video || !this.selfieSegmentation) return;

      if (this.video.readyState >= 2) {
        await this.selfieSegmentation.send({ image: this.video });
      }

      this.animationId = requestAnimationFrame(processFrame);
    };

    processFrame();

    // Создаем выходной stream
    this.processedStream = this.canvas.captureStream(30);

    // Добавляем аудио треки
    const audioTracks = sourceStream.getAudioTracks();
    audioTracks.forEach(track => {
      this.processedStream!.addTrack(track.clone());
    });

    return this.processedStream;
  }

  async stop(): Promise<void> {
    this.isRunning = false;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.video) {
      this.video.pause();
      this.video.srcObject = null;
      this.video = null;
    }

    if (this.selfieSegmentation) {
      await this.selfieSegmentation.close();
      this.selfieSegmentation = null;
    }

    if (this.processedStream) {
      this.processedStream.getTracks().forEach(track => track.stop());
      this.processedStream = null;
    }

    this.canvas = null;
    this.ctx = null;
    this.blurCanvas = null;
    this.blurCtx = null;
  }

  getProcessedStream(): MediaStream | null {
    return this.processedStream;
  }

  isActive(): boolean {
    return this.isRunning;
  }
}

// Singleton для глобального использования
let globalProcessor: BackgroundBlurProcessor | null = null;

export function getBackgroundBlurProcessor(): BackgroundBlurProcessor {
  if (!globalProcessor) {
    globalProcessor = new BackgroundBlurProcessor(15);
  }
  return globalProcessor;
}

export async function applyBackgroundBlur(
  stream: MediaStream, 
  enabled: boolean
): Promise<MediaStream> {
  const processor = getBackgroundBlurProcessor();
  
  if (enabled) {
    return processor.start(stream);
  } else {
    if (processor.isActive()) {
      await processor.stop();
    }
    return stream;
  }
}
